import { EventBus, useGameStore, wireAppearanceJson } from '@campus/core';
import type { UserStatus } from '@campus/core';
import type { ZoneScene } from '../game/scenes/ZoneScene.js';
import { LiveKitService } from '../services/LiveKitService.js';
import { ColyseusClient } from '../game/systems/ColyseusClient.js';
import { getTenantSlug } from '../lib/tenant.js';
import { API_BASE } from '../lib/apiBase.js';
import { playWhiz } from '../lib/whiz.js';
import { jwtExpired } from '../lib/jwtRole.js';
import { askNotificationPermissionOnce } from '../lib/mediaReminder.js';
import { useMembersStore, sessionInfo } from '../ui/people/membersStore.js';
import { useRoomClaimsStore } from '../stores/roomClaimsStore.js';
import { useBubbleChatStore } from '../stores/bubbleChatStore.js';
import { recordReactionUse } from '../lib/reactionUsage.js';

let initialized = false;
export const liveKitService = new LiveKitService();
export const colyseusClient = new ColyseusClient();

/**
 * GameBridge — Point de couplage unique entre EventBus et Zustand/Services.
 *
 * RÈGLE ABSOLUE : C'est le seul fichier où EventBus et useGameStore se touchent.
 * C'est aussi le seul fichier qui connaît LiveKitService.
 *
 * Responsabilités :
 *   - EventBus Phaser/Colyseus → Zustand (état UI)
 *   - EventBus → LiveKitService (souscriptions audio)
 *   - EventBus broadcast → fetch token + connexion room broadcast
 */
export function initGameBridge(): void {
  if (initialized) return;
  initialized = true;

  const store = useGameStore.getState;
  liveKitService.setDeafened(isDeafStatus(store().localStatus));

  // ── Scène Phaser → ColyseusClient ────────────────────────
  // scene:ready est émis dans ZoneScene.create(), juste après zone:entered.
  // La connexion Colyseus est async → la scène sera prête avant le premier patch.
  EventBus.on('scene:ready', ({ scene }) => {
    colyseusClient.setScene(scene as unknown as ZoneScene);
  });

  // ── Zone ─────────────────────────────────────────────────
  EventBus.on('zone:entered', async ({ zoneId, zoneName, spawnX, spawnY }) => {
    // Guard : ne pas reconnecter si l'utilisateur a été kické
    if (store().authState !== 'in-game') {
      console.warn('[GameBridge] zone:entered ignoré — authState:', store().authState);
      return;
    }
    store().setCurrentZone(zoneId, zoneName);

    // zoneId = "{tenantId}:{zoneSlug}" (voir ZoneConfig.id)
    const colonIdx = zoneId.indexOf(':');
    if (colonIdx === -1) return;
    const tenantId = zoneId.slice(0, colonIdx);
    const slug     = zoneId.slice(colonIdx + 1);
    const jwt      = store().jwt;
    if (!jwt) { console.warn('[GameBridge] JWT absent — Colyseus non connecté'); return; }

    // Connexion Colyseus (non bloquante). Échec (serveur temps réel en cours
    // de redémarrage…) → même rattrapage qu'une connexion perdue.
    const displayName = store().displayName ?? zoneName;
    const avatarIndex = store().avatarIndex;
    const appearanceJson = wireAppearanceJson(store().appearance);
    stopReconnecting();
    store().setRealtimeStatus('connected');
    void colyseusClient.connect(tenantId, slug, jwt, displayName, avatarIndex, appearanceJson, spawnX, spawnY)
      .then((result) => { if (result === 'failed') startReconnecting(); });

    await connectZoneMedia(tenantId, slug, jwt, true);
  });

  // ── Reconnexion temps réel ───────────────────────────────
  EventBus.on('colyseus:connection-lost', () => startReconnecting());

  EventBus.on('transition:started', async ({ toZoneId, spawnName }) => {
    store().setTransitioning(true);
    stopReconnecting();

    // 1. Mute avant la transition
    await liveKitService.mute();
    // 2. Déconnexion Colyseus (synchrone)
    colyseusClient.disconnect();
    // 3. Déconnexion LiveKit zone
    await liveKitService.disconnectFromZone();
    store().setLiveKitConnected(false);

    // 4. Extraire le slug cible : toZoneId = "{tenantId}:{slug}"
    const colonIdx = toZoneId.indexOf(':');
    const targetSlug = colonIdx !== -1 ? toZoneId.slice(colonIdx + 1) : toZoneId;
    store().setTargetZoneSlug(targetSlug);
    store().setTargetSpawnName(spawnName ?? null);

    // 5. Relancer BootScene → chargement de la nouvelle zone
    EventBus.emit('game:restart-scene', {});
    // isTransitioning sera remis à false par setCurrentZone() dans zone:entered
  });
  EventBus.on('transition:complete', () => {
    store().setTransitioning(false);
  });

  // ── Mouvement joueur local → Colyseus ─────────────────────
  EventBus.on('player:moved', ({ x, y, direction, animation }) => {
    colyseusClient.sendMove(x, y, direction, animation);
  });
  EventBus.on('scooter:mount-request', (req) => colyseusClient.sendScooterMount(req));
  EventBus.on('scooter:dismount-request', () => colyseusClient.sendScooterDismount());
  EventBus.on('seat:sit-request', (req) => colyseusClient.sendSeatSit(req));
  EventBus.on('seat:stand-request', () => colyseusClient.sendSeatStand());
  EventBus.on('elevator:board-request', (req) => colyseusClient.sendElevatorBoard(req));
  EventBus.on('elevator:arrive-request', (req) => colyseusClient.sendElevatorArrive(req));

  // ── Périphériques audio/vidéo (Réglages) ──────────────────
  // Applique en direct sur les rooms déjà connectées ; sinon la préférence
  // (déjà persistée dans le store par SettingsModal) s'appliquera au
  // prochain connect() via LiveKitService.buildRoomOptions().
  EventBus.on('ui:device-changed', ({ kind, deviceId }) => {
    void liveKitService.switchDevice(kind, deviceId);
  });

  // ── Proximité → LiveKit souscriptions ────────────────────
  EventBus.on('player:nearby', (player) => {
    store().addNearbyPlayer(player);
    // Recalculer les souscriptions LiveKit à chaque changement de voisinage
    liveKitService.updateSubscriptions(
      new Set([...store().nearbyPlayers.keys()]),
      store().localBubbleId,
      store().playerBubbleIds,
    );
  });
  EventBus.on('player:left-proximity', ({ userId }) => {
    store().removeNearbyPlayer(userId);
    liveKitService.updateSubscriptions(
      new Set([...store().nearbyPlayers.keys()]),
      store().localBubbleId,
      store().playerBubbleIds,
    );
  });

  // ── Joueurs zone → Zustand ────────────────────────────────
  EventBus.on('colyseus:zone-players-updated', (players) => {
    store().setZonePlayers(players);
    console.log('[GameBridge] zonePlayers updated:', players.size);
  });

  // ── Bulles Colyseus → LiveKit souscriptions ──────────────
  EventBus.on('colyseus:bubble-ids-updated', (allBubbleIds) => {
    store().setPlayerBubbleIds(allBubbleIds);
    liveKitService.updateSubscriptions(
      new Set([...store().nearbyPlayers.keys()]),
      store().localBubbleId,
      allBubbleIds,
    );
    // Recalculer le compteur si le joueur local est dans une bulle
    const localBubble = store().localBubbleId;
    if (localBubble) {
      const count = [...allBubbleIds.entries()]
        .filter(([, bid]) => bid === localBubble)
        .length - 1; // -1 : exclure le joueur local
      store().setProximityCount(Math.max(0, count));
    }
  });
  // Discussion de la bulle (ou de proximité) gardée un moment après en être
  // sorti : une reconnexion ou un pas hors de la salle ne l'efface plus.
  let chatBubbleId: string | null = null;
  let chatClearTimer: ReturnType<typeof setTimeout> | null = null;
  const cancelChatClear = () => {
    if (chatClearTimer) { clearTimeout(chatClearTimer); chatClearTimer = null; }
  };
  const scheduleChatClear = () => {
    if (chatClearTimer) return;
    chatClearTimer = setTimeout(() => {
      chatClearTimer = null;
      if (store().localBubbleId || store().proximityCount > 0) return;
      store().clearProximityMessages();
      chatBubbleId = null;
    }, BUBBLE_CHAT_GRACE_MS);
  };

  EventBus.on('colyseus:local-bubble-changed', ({ bubbleId }) => {
    store().setLocalBubbleId(bubbleId);
    liveKitService.updateSubscriptions(
      new Set([...store().nearbyPlayers.keys()]),
      bubbleId,
      store().playerBubbleIds,
    );
  });

  // ── Compteur proximité (bouton Joueurs du HUD) ────────────
  // En bulle fixe  : nombre de joueurs dans la même bubbleId (hors local)
  // Hors bulle     : nombre de joueurs dans le rayon Colyseus (proximity:changed)
  EventBus.on('bubble:changed', ({ previousBubbleId, newBubbleId, newType }) => {
    if (useBubbleChatStore.getState().meeting?.bubbleId !== newBubbleId) useBubbleChatStore.setState({ meeting: null });
    // Type de la bulle locale (ex: distinguer meeting-room d'une bulle
    // normale côté UI, voir RecordingButton.tsx) — newType vaut '' hors
    // bulle (jamais un type réel), donc || null normalise correctement.
    store().setLocalBubbleType(newBubbleId ? (newType || null) : null);
    // Chaque bulle repart d'un chat vide — la persistance reste réservée
    // au chat général (Postgres). Sans ça, les messages de la bulle
    // précédente réapparaîtraient dans la nouvelle (proximityMessages
    // n'est jamais scopé par bubbleId, voir ProximityChatPanel.tsx).
    // Mais revenir dans la même bulle garde la discussion : après une
    // reconnexion (déploiement), le serveur annonce l'entrée dans la salle où
    // l'on était déjà, et le chat se vidait (Copyhouse, Jordan, 2026-11-02).
    if (newBubbleId) {
      cancelChatClear();
      if (newBubbleId !== chatBubbleId) store().clearProximityMessages();
      chatBubbleId = newBubbleId;
    } else if (previousBubbleId !== newBubbleId) {
      scheduleChatClear();
    }
    if (newBubbleId) {
      // Entrer dans une bulle : compter les autres joueurs avec le même bubbleId
      const count = [...store().playerBubbleIds.entries()]
        .filter(([, bid]) => bid === newBubbleId)
        .length - 1; // -1 pour exclure le joueur local lui-même
      store().setProximityCount(Math.max(0, count));
    } else {
      // Sortir d'une bulle : remettre à 0, proximity:changed va prendre le relais
      store().setProximityCount(0);
    }
  });

  EventBus.on('proximity:changed', ({ nearbyUserIds }) => {
    store().setProximityUserIds(nearbyUserIds);
    // N'actualiser le compteur que si le joueur local est hors bulle.
    // En bulle, c'est colyseus:bubble-ids-updated qui fait autorité.
    if (!store().localBubbleId) {
      store().setProximityCount(nearbyUserIds.length);
      // Plus personne à proximité et pas en bulle → la conversation est
      // terminée, on vide le chat pour que la prochaine rencontre reparte
      // à zéro. Couvre la proximité pure (bubbleId reste '' en permanence,
      // donc jamais couverte par le clear sur bubble:changed ci-dessus).
      if (nearbyUserIds.length === 0) {
        scheduleChatClear();
      } else {
        cancelChatClear();
        // Sorti d'une salle et quelqu'un à côté : nouvelle discussion, pas celle de la salle.
        if (chatBubbleId !== null) { store().clearProximityMessages(); chatBubbleId = null; }
      }
    }
  });

  // ── LiveKit état ─────────────────────────────────────────
  EventBus.on('livekit:disconnected', () => {
    store().setLiveKitConnected(false);
  });
  EventBus.on('livekit:speakers-changed', ({ speakerUserIds }) => {
    store().setActiveSpeakers(speakerUserIds);
  });

  // ── Broadcast pattern Ding-Dong ──────────────────────────
  EventBus.on('broadcast:started', async ({ adminUserId: _admin }) => {
    store().setBroadcastActive(true);
    store().openModal('broadcast-incoming');

    // Fetch du token broadcast en parallèle du jingle (joué côté UI)
    try {
      const currentZoneId = store().currentZoneId ?? '';
      const broadcastTenantId = currentZoneId.slice(0, currentZoneId.indexOf(':'));
      const res = await fetch(`${API_BASE}/media/token/broadcast`, {
        headers: { Authorization: `Bearer ${store().jwt}`, 'X-Tenant-ID': broadcastTenantId },
      });
      const { token, url } = await res.json() as { token: string; url: string };
      // connectToBroadcast attend room.connected — pas de setTimeout
      await liveKitService.connectToBroadcast(token, url);
    } catch (err) {
      console.warn('[GameBridge] Broadcast connection failed:', err);
      store().setBroadcastActive(false);
    }
  });

  EventBus.on('broadcast:ended', async () => {
    store().setBroadcastActive(false);
    store().closeModal();
    await liveKitService.disconnectFromBroadcast();
  });

  // ── Modération ───────────────────────────────────────────
  EventBus.on('moderation:kicked', () => {
    // Guard : ignorer si déjà en cours de déconnexion (le LEAVE_ROOM protocol
    // re-émet moderation:kicked après le premier traitement). 'replaced' : le
    // LEAVE_ROOM qui suit session:replaced n'est pas une expulsion.
    if (store().authState === 'unauthenticated' || store().authState === 'replaced') return;
    console.log('[GameBridge:mod] moderation:kicked reçu — déconnexion immédiate');

    // 1. Changer authState IMMÉDIATEMENT pour bloquer toute reconnexion
    //    (BootScene et zone:entered vérifient authState avant de se connecter)
    store().setAuthState('unauthenticated');

    // 2. Déconnecter proprement Colyseus + LiveKit
    colyseusClient.disconnect();
    void liveKitService.disconnectFromZone();

    // 3. Vider le JWT pour empêcher toute requête authentifiée
    store().setJwt(null as unknown as string);
    store().clearCurrentZone();
  });

  // Mot de passe changé depuis une autre fenêtre (AuthService.completePasswordSetup
  // → revokeAllSessions + kick temps réel, voir ZoneRoom.disconnectRevokedSession) —
  // même flux de déconnexion que moderation:kicked, mais message dédié : ce
  // n'est pas une expulsion, l'utilisateur peut se reconnecter immédiatement
  // avec son nouveau mot de passe.
  EventBus.on('auth:session-revoked', ({ reason }) => {
    if (store().authState === 'unauthenticated') return;
    console.log(`[GameBridge:auth] session révoquée (${reason ?? 'password-changed'}) — déconnexion`);

    store().setAuthState('unauthenticated');
    colyseusClient.disconnect();
    void liveKitService.disconnectFromZone();
    store().setJwt(null as unknown as string);
    store().clearCurrentZone();

    EventBus.emit('ui:toast', {
      message: reason === 'role-changed'
        ? 'Ton rôle sur cet espace a changé - reconnecte-toi pour en profiter.'
: reason === 'access-changed'
          ? 'Les lieux auxquels tu as accès ont changé - reconnecte-toi.'
          : reason === 'session-expired'
            ? 'Ta session a expiré - reconnecte-toi.'
            : 'Ton mot de passe a été changé depuis une autre fenêtre - reconnecte-toi.',
      duration: 6000,
      tone: 'warning',
    });
  });

  // Même compte entré dans le campus depuis une autre fenêtre ou un autre
  // appareil (ZoneRoom.replaceOlderSessions) : on quitte le campus sans
  // oublier le JWT — l'écran SessionReplacedPage propose « Revenir ici »,
  // qui repasse en 'in-game' (même carte, même position : lastPlayerPosition
  // est enregistré au démontage du jeu).
  EventBus.on('auth:session-replaced', () => {
    if (store().authState !== 'in-game') return;
    console.log('[GameBridge:auth] campus ouvert ailleurs — sortie du campus');
    store().setAuthState('replaced');
    colyseusClient.disconnect();
    void liveKitService.disconnectFromZone();
    // La bulle et le chat de proximité appartiennent à la connexion évincée.
    store().setLocalBubbleId(null);
    store().clearProximityMessages();
  });

  EventBus.on('auth:logout-requested', () => { void logout(); });

  // Verrou du micro sur toute la durée du mute serveur (avant : 2 min au plus,
  // le chat restait bloqué 5 min). Renvoyé par le serveur à chaque connexion
  // tant que le mute court : un rechargement ne le lève plus.
  EventBus.on('moderation:muted', async ({ durationMs }) => {
    console.log(`[GameBridge:mod] moderation:muted reçu — durationMs=${durationMs}`);
    const alreadyMuted = (store().mutedUntil ?? 0) > Date.now();
    store().setMutedUntil(Date.now() + durationMs);
    // Couper le micro LiveKit et verrouiller
    await liveKitService.mute();
    liveKitService.setAdminMuteLock(durationMs);
    store().setLocalMuted(true);
    if (!alreadyMuted) {
      EventBus.emit('ui:toast', { message: 'Un admin a coupé ton micro et ton chat pour quelques minutes.', tone: 'warning' });
    }
  });
  // Micro ou caméra coupé par un admin depuis une vignette : la personne peut
  // les rallumer (pas de verrou, contrairement à moderation:muted).
  EventBus.on('moderation:media-off', async ({ kind }) => {
    const mic = kind === 'mic' ? false : !store().isLocalMuted;
    const camera = kind === 'camera' ? false : !store().isLocalCameraOff;
    const result = await liveKitService.setLocalMedia({ mic, camera });
    store().setLocalMuted(!result.mic);
    store().setLocalCameraOff(!result.camera);
    EventBus.emit('ui:toast', {
      message: kind === 'mic' ? 'Un admin a coupé ton micro. Tu peux le rallumer.' : 'Un admin a coupé ta caméra. Tu peux la rallumer.',
      tone: 'info',
    });
  });
  EventBus.on('moderation:unmuted', () => {
    const wasMuted = (store().mutedUntil ?? 0) > Date.now();
    store().setMutedUntil(null);
    liveKitService.clearAdminMuteLock();
    // Le micro reste coupé : la personne le rallume quand elle veut.
    if (wasMuted) EventBus.emit('ui:toast', { message: 'Tu peux de nouveau parler.', tone: 'success' });
  });

  // ── Chat proximity ───────────────────────────────────────
  // Remplace le timestamp serveur par l'heure de réception client
  // pour éviter tout décalage d'horloge serveur/client qui expirerait
  // la bulle (TTL 5s) avant même son affichage.
  EventBus.on('chat:proximity-received', (msg) => {
    // DEBUG — retirer une fois la chaîne confirmée
    console.log('[GameBridge] chat:proximity-received reçu, ajout au store:', msg);
    store().addProximityMessage({ ...msg, timestamp: Date.now() });
    console.log('[GameBridge] proximityMessages après ajout:', store().proximityMessages.length);
  });

  // ── Contrôles média (HUD → LiveKit) ──────────────────────
  // Absent, Occupé, Focus : micro et caméra restent coupés (on les rallume
  // en repassant Disponible).
  EventBus.on('ui:toggle-mic', async () => {
    if (store().isLocalMuted && blockMediaForStatus('micro')) return;
    // Dans le geste qui allume : seul moment où le navigateur accepte la demande.
    if (store().isLocalMuted) askNotificationPermissionOnce();
    const muted = await liveKitService.toggleMic();
    store().setLocalMuted(muted);
  });
  EventBus.on('ui:toggle-camera', async () => {
    if (store().isLocalCameraOff && blockMediaForStatus('caméra')) return;
    if (store().isLocalCameraOff) askNotificationPermissionOnce();
    const cameraOff = await liveKitService.toggleCamera();
    store().setLocalCameraOff(cameraOff);
  });

  // ── Partage d'écran ─────────────────────────────────────
  EventBus.on('ui:toggle-screenshare', async () => {
    const sharing = await liveKitService.toggleScreenShare();
    store().setScreenSharing(sharing);
  });

  // ── Envoi chat bulle (React UI → Colyseus) ───────────────
  EventBus.on('ui:chat-bubble-send', ({ text }) => {
    colyseusClient.sendChat(text);
  });
  EventBus.on('ui:meeting-chat-pin', ({ messageId }) => colyseusClient.sendMeetingChatPin(messageId));
  EventBus.on('ui:meeting-chat-lock', ({ locked }) => colyseusClient.sendMeetingChatLock(locked));

  // ── Chat de salle de réunion : la séance en cours à l'arrivée (comme Meet) ──
  // Remplace la discussion locale : après une reconnexion, l'historique du
  // serveur fait foi (les non-lus déjà comptés sont gardés).
  EventBus.on('meeting-chat:state', ({ bubbleId, messages, pinnedId, locked }) => {
    const me = sessionInfo().userId;
    const unread = useBubbleChatStore.getState().unread;
    const same = chatBubbleId === bubbleId && store().proximityMessages.length > 0;
    store().setProximityMessages(messages.map((m) => ({ ...m, isSelf: m.senderId === me })));
    useBubbleChatStore.setState({ meeting: { bubbleId, pinnedId, locked }, ...(same ? { unread } : {}) });
  });
  EventBus.on('meeting-chat:settings', (meeting) => {
    useBubbleChatStore.setState({ meeting });
  });

  // ── Statut utilisateur ─────────────────────────────────────
  EventBus.on('ui:status-change', ({ status }) => {
    store().setLocalStatus(status);
    colyseusClient.sendStatus(status);
    void applyStatusMedia(status);
  });

  // ── Modération (badge muté visible par tous) ───────────────
  EventBus.on('moderation:player-muted', ({ userId }) => {
    console.log(`[GameBridge:mod] player-muted → addMutedPlayerId(${userId})`);
    store().addMutedPlayerId(userId);
    console.log(`[GameBridge:mod] mutedPlayerIds après ajout:`, [...store().mutedPlayerIds]);
  });
  EventBus.on('moderation:player-unmuted', ({ userId }) => {
    console.log(`[GameBridge:mod] player-unmuted → removeMutedPlayerId(${userId})`);
    store().removeMutedPlayerId(userId);
  });

  // ── Portes ──────────────────────────────────────────────────
  EventBus.on('door:nearby', (data) => {
    store().setNearbyDoor(data);
  });
  EventBus.on('door:left', () => {
    store().setNearbyDoor(null);
  });

  // ── Zones area (iframe overlay / launch prompt) ────────────
  EventBus.on('ui:area-entered', (data) => {
    store().setActiveArea({ ...data, action: 'iframe' });
    store().setIframeDismissed(false);
  });
  EventBus.on('ui:area-launch', (data) => {
    store().setActiveArea({ ...data, action: 'launch' });
  });
  EventBus.on('ui:area-exited', () => {
    store().setActiveArea(null);
    store().setIframeDismissed(false);
  });

  // ── Wave ───────────────────────────────────────────────────
  EventBus.on('ui:wave-send', ({ targetUserId }) => {
    colyseusClient.sendWave(targetUserId);
  });
  EventBus.on('wave:received', ({ fromUserId, fromDisplayName }) => {
    // Ignorer les waves en mode focus (sauf broadcast admin)
    if (store().localStatus === 'focus') return;
    const wave = { fromUserId, fromDisplayName, timestamp: Date.now() };
    store().addWaveNotification(wave);
    // Auto-remove après 3s
    setTimeout(() => { store().removeWaveNotification(fromUserId); }, 3000);
  });

  // ── Whiz : l'écran tremble avec un petit son (jamais en Focus) ──
  EventBus.on('whiz:received', ({ fromUserId }) => {
    if (store().localStatus === 'focus') return;
    const name = useMembersStore.getState().members.get(fromUserId)?.displayName ?? 'Quelqu\u2019un';
    EventBus.emit('ui:toast', { message: `${name} t'envoie un whiz !`, tone: 'info', duration: 5000 });
    playWhiz(store().settings.reduceMotion);
  });

  // ── Focus blocked — toast discret pour l'expediteur ──────────
  EventBus.on('ui:focus-blocked', ({ targetName }) => {
    const name = targetName || 'Cette personne';
    EventBus.emit('ui:toast', { message: `${name} est en Focus et ne reçoit pas de notification pour l'instant.`, duration: 3000 });
  });

  // ── Réactions emoji ──────────────────────────────────────────
  EventBus.on('ui:reaction-send', ({ emoji }) => {
    recordReactionUse(emoji);
    colyseusClient.sendReaction(emoji);
  });

  // ── Carte désactivée pendant qu'on y est → carte d'arrivée ──
  // (cible vide = carte d'arrivée, résolue par BootScene ; la carte fermée
  // n'est plus dans la liste renvoyée par l'API).
  EventBus.on('zone:closed', () => {
    if (store().authState !== 'in-game' || store().isTransitioning) return;
    EventBus.emit('ui:toast', { message: 'Cette carte vient d\'être fermée. Direction l\'arrivée.', tone: 'info' });
    EventBus.emit('zone:transition-requested', { targetSlug: '', spawnId: 'default' });
  });

  // ── Zone navigation (depuis HUD) ──────────────────────────
  EventBus.on('zone:transition-requested', ({ targetSlug, spawnId }) => {
    const tenantId = store().currentZoneId?.split(':')[0];
    if (!tenantId) return;
    const toZoneId = `${tenantId}:${targetSlug}`;
    EventBus.emit('transition:started', {
      fromZoneId: store().currentZoneId ?? '',
      toZoneId,
      spawnName: spawnId,
    });
  });

  // ── Profil update ──────────────────────────────────────────
  EventBus.on('ui:profile-update', ({ displayName, avatarIndex }) => {
    colyseusClient.sendProfileUpdate(displayName, avatarIndex);
    store().setProfile(displayName, avatarIndex);
  });

  // ── Appearance update ──────────────────────────────────────
  EventBus.on('ui:appearance-update', ({ appearance }) => {
    colyseusClient.sendAppearanceUpdate(wireAppearanceJson(appearance));
    store().setAppearance(appearance);
  });

  // player:profile-updated est routé directement vers ZoneScene via EventBus
  // (handler dans finishCreate). Pas besoin de passer par GameBridge.

  // ── Verrouillage bulle ─────────────────────────────────────
  // ── Tableaux blancs ──
  EventBus.on('ui:board-joined', ({ itemId }) => { colyseusClient.sendBoardOpen(itemId); });
  EventBus.on('ui:board-left', () => { colyseusClient.sendBoardClose(); });
  EventBus.on('ui:board-update', ({ itemId, elements }) => { colyseusClient.sendBoardUpdate(itemId, elements); });
  EventBus.on('ui:board-pointer', ({ itemId, x, y, button }) => { colyseusClient.sendBoardPointer(itemId, x, y, button); });

  EventBus.on('ui:lock-bubble', ({ bubbleId, locked }) => {
    colyseusClient.sendLockBubble(bubbleId, locked);
  });
  // Synchroniser le store avec l'état serveur (tous les clients)
  EventBus.on('bubble:lock-changed', ({ bubbleId, locked, ownerUserId, ownerUserIds }) => {
    if (locked) {
      store().addLockedBubble(bubbleId);
      if (ownerUserIds) store().setLockedBubbleOwners(bubbleId, ownerUserIds);
      else if (ownerUserId) store().setLockedBubbleOwner(bubbleId, ownerUserId);
    } else {
      store().removeLockedBubble(bubbleId);
      store().removeLockedBubbleOwner(bubbleId);
    }
  });

  // ── Enregistrement (bandeau RGPD) ───────────────────────────
  // Salles revendiquées par un duo (portes, murs invisibles, invite « Revendiquer »).
  EventBus.on('claims:changed', ({ claims }) => { useRoomClaimsStore.getState().setClaims(claims); });

  EventBus.on('recording:changed', ({ mapZoneId, recording, startedAt }) => {
    store().setRecordingActive(mapZoneId, recording, startedAt ? Date.parse(startedAt) : undefined);
  });

  // ── Knock ──────────────────────────────────────────────────
  EventBus.on('ui:knock-request', ({ bubbleId }) => {
    colyseusClient.sendKnockRequest(bubbleId);
  });

  EventBus.on('knock:request', (data) => {
    store().addKnockRequest(data);
  });

  EventBus.on('ui:knock-respond', ({ userId, bubbleId, granted }) => {
    colyseusClient.sendKnockRespond(userId, bubbleId, granted);
    store().removeKnockRequest(userId);
  });

  EventBus.on('knock:granted', () => {
    // Toast handled in React overlay
  });

  EventBus.on('knock:denied', () => {
    // Toast handled in React overlay
  });

  // ── Bureau personnel (Sprint 11a) ──────────────────────────
  EventBus.on('desk:entered', (data) => {
    store().setNearbyDesk(data);
    // Toast si quelqu'un arrive à MON bureau
    const myDesk = store().localDeskId;
    if (myDesk && data.deskId === myDesk && data.ownerId) {
      // Handled by desk:visitor-arrived
    }
  });
  EventBus.on('desk:exited', () => {
    store().setNearbyDesk(null);
  });
  EventBus.on('desk:assignments-loaded', ({ assignments }) => {
    store().setDeskAssignments(assignments);
  });
  EventBus.on('desk:visitor-arrived', ({ visitorName }) => {
    EventBus.emit('ui:toast', { message: `${visitorName} arrive à ton bureau`, duration: 4000 });
  });
  EventBus.on('ui:desk-claim', async ({ deskId, zoneSlug }) => {
    const jwt = store().jwt;
    if (!jwt) return;
    const tenantSlug = getTenantSlug();
    try {
      const res = await fetch(`${API_BASE}/world/desks/claim`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${jwt}`,
          'X-Tenant-ID': tenantSlug,
        },
        body: JSON.stringify({ deskId, zoneSlug }),
      });
      if (res.ok) {
        const result = await res.json() as { deskId: string; userId: string; displayName: string };
        store().setLocalDeskId(result.deskId);
        // Refresh assignments
        EventBus.emit('ui:toast', { message: 'Ce bureau est à toi.', tone: 'success' });
      } else {
        EventBus.emit('ui:toast', { message: 'Ce bureau est déjà pris.', tone: 'warning' });
      }
    } catch { /* non-critique */ }
  });
  EventBus.on('ui:desk-release', async ({ deskId, zoneSlug }) => {
    const jwt = store().jwt;
    if (!jwt) return;
    const tenantSlug = getTenantSlug();
    try {
      await fetch(`${API_BASE}/world/desks/claim`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${jwt}`,
          'X-Tenant-ID': tenantSlug,
        },
        body: JSON.stringify({ deskId, zoneSlug }),
      });
      store().setLocalDeskId(null);
      EventBus.emit('ui:toast', { message: 'Bureau libéré.' });
    } catch { /* non-critique */ }
  });

  // ── Podium (Sprint 11d) ───────────────────────────────────
  EventBus.on('podium:entered', () => {
    store().setInPodiumZone(true);
    colyseusClient.sendPodiumEnter();
  });
  EventBus.on('podium:exited', () => {
    store().setInPodiumZone(false);
    colyseusClient.sendPodiumLeave();
  });
  EventBus.on('podium:state', ({ speakerId, speakerName }) => {
    store().setPodiumSpeaker(speakerId, speakerName);
    if (!speakerId) {
      store().clearRaisedHands();
    }
  });
  EventBus.on('podium:hand-raised', ({ userId, displayName }) => {
    store().addRaisedHand({ userId, displayName });
  });
  EventBus.on('ui:hand-raise', () => {
    colyseusClient.sendHandRaise();
  });
  EventBus.on('ui:hand-grant', ({ userId }) => {
    colyseusClient.sendHandGrant(userId);
    store().removeRaisedHand(userId);
  });
  EventBus.on('podium:mute-audience', ({ speakerId }) => {
    // Mute si on n'est pas le speaker
    const jwt = store().jwt;
    if (!jwt) return;
    // Decode userId from JWT
    try {
      const payload = JSON.parse(atob(jwt.split('.')[1]!));
      if (payload.sub !== speakerId) {
        void liveKitService.mute();
        store().setLocalMuted(true);
      }
    } catch { /* non-critique */ }
  });

  // NOTE : 'overlay:positions-updated' n'est PAS routé vers Zustand.
  // Les positions x/y ne rentrent jamais dans React.
}

// ── Reconnexion automatique du temps réel ──────────────────
//
// La connexion Colyseus tombe à chaque redémarrage de campus-realtime
// (déploiement), coupure réseau ou mise en veille. Sans reconnexion, la carte
// restait affichée mais morte (plus personne ne bouge, personne ne nous voit,
// chat coupé) jusqu'à un rechargement de la page.
// Essais à intervalles croissants, immédiatement au retour du réseau ; après
// RECONNECT_FAILED_AFTER_MS, le bandeau propose de recharger (les essais
// continuent). Départs voulus (expulsion, session révoquée ou remplacée,
// changement de carte, déconnexion) : jamais de reconnexion.

/** Discussion de bulle gardée ce temps après en être sorti (reconnexion, pas de travers). */
const BUBBLE_CHAT_GRACE_MS = 60_000;
const RECONNECT_DELAYS_MS = [1_000, 2_000, 4_000, 8_000, 15_000];
const RECONNECT_FAILED_AFTER_MS = 60_000;
let reconnectRun = 0;
let reconnecting = false;
let wakeReconnect: (() => void) | null = null;

function waitOrWake(ms: number): Promise<void> {
  return new Promise((resolve) => {
    const done = () => {
      clearTimeout(timer);
      window.removeEventListener('online', done);
      if (wakeReconnect === done) wakeReconnect = null;
      resolve();
    };
    const timer = setTimeout(done, ms);
    window.addEventListener('online', done);
    wakeReconnect = done;
  });
}

function startReconnecting(): void {
  const store = useGameStore.getState;
  if (reconnecting || store().authState !== 'in-game') return;
  reconnecting = true;
  const run = ++reconnectRun;
  const startedAt = Date.now();
  store().setRealtimeStatus('reconnecting');
  console.warn('[GameBridge] connexion temps réel perdue — reconnexion automatique');

  void (async () => {
    for (let attempt = 0; ; attempt++) {
      const base = RECONNECT_DELAYS_MS[Math.min(attempt, RECONNECT_DELAYS_MS.length - 1)]!;
      // Un peu d'aléa : après un déploiement, tout le monde ne revient pas à la même milliseconde.
      await waitOrWake(base + Math.random() * 500);
      if (run !== reconnectRun) return;

      const jwt = store().jwt;
      if (store().authState !== 'in-game' || !jwt) { stopReconnecting(); return; }
      // Jeton expiré : le serveur refusera toujours (TOKEN_EXPIRED). Avant, un
      // onglet resté ouvert plus de 7 jours réessayait toutes les 16 s sans fin.
      if (jwtExpired(jwt)) {
        stopReconnecting();
        EventBus.emit('auth:session-revoked', { reason: 'session-expired' });
        return;
      }

      const result = await colyseusClient.reconnect(jwt);
      if (run !== reconnectRun) return;
      if (result === 'superseded') { stopReconnecting(); return; }
      if (result === 'joined') {
        stopReconnecting();
        console.log(`[GameBridge] connexion temps réel rétablie (essai ${attempt + 1})`);
        EventBus.emit('ui:toast', { message: 'Connexion rétablie.', duration: 3000, tone: 'success' });
        // LiveKit a sa propre reconnexion ; s'il a fini par lâcher (coupure
        // réseau longue), on le reconnecte aussi, micro et caméra coupés.
        const zoneId = store().currentZoneId;
        const colon = zoneId?.indexOf(':') ?? -1;
        if (!store().liveKitConnected && zoneId && colon !== -1) {
          void connectZoneMedia(zoneId.slice(0, colon), zoneId.slice(colon + 1), jwt, false);
        }
        return;
      }
      if (Date.now() - startedAt > RECONNECT_FAILED_AFTER_MS && store().realtimeStatus !== 'failed') {
        store().setRealtimeStatus('failed');
      }
    }
  })();
}

function stopReconnecting(): void {
  reconnectRun++;
  reconnecting = false;
  wakeReconnect?.();
  if (useGameStore.getState().realtimeStatus !== 'connected') {
    useGameStore.getState().setRealtimeStatus('connected');
  }
}

/** Bouton « Réessayer » du bandeau : tente tout de suite sans attendre le délai. */
export function retryRealtimeNow(): void {
  wakeReconnect?.();
}

// ── Statuts : micro, caméra et écoute ─────────────────────────
// Disponible : tout normal. Absent : micro et caméra coupés, on entend
// toujours. Occupé et Focus : micro et caméra coupés, on n'entend plus
// personne (LiveKitService.setDeafened). En repassant Disponible, micro et
// caméra reviennent comme avant le premier statut « silencieux ».

/** Occupé et Focus : on n'entend plus les gens à côté ni dans sa salle. */
function isDeafStatus(status: UserStatus): boolean {
  return status === 'busy' || status === 'focus';
}

/** Micro et caméra avant de quitter Disponible (null : rien à rétablir). */
let mediaBeforeQuietStatus: { mic: boolean; camera: boolean } | null = null;
/** Compteur de génération : un changement de statut plus récent l'emporte. */
let statusMediaGeneration = 0;

async function applyStatusMedia(status: UserStatus): Promise<void> {
  const store = useGameStore.getState;
  const gen = ++statusMediaGeneration;
  liveKitService.setDeafened(isDeafStatus(status));

  // Absent automatique (onglet caché) : simple statut affiché aux autres,
  // micro et caméra ne bougent pas — on change souvent d'onglet pour
  // travailler (retour de Charles, coaching du 2026-10-28). Seul un Absent
  // choisi à la main les coupe.
  if (status === 'away' && store().awayReason === 'tab-hidden') return;

  if (status !== 'available') {
    mediaBeforeQuietStatus ??= { mic: !store().isLocalMuted, camera: !store().isLocalCameraOff };
    if (store().isLocalMuted && store().isLocalCameraOff) return;
    await liveKitService.setLocalMedia({ mic: false, camera: false });
    if (gen !== statusMediaGeneration) return;
    store().setLocalMuted(true);
    store().setLocalCameraOff(true);
    return;
  }

  const before = mediaBeforeQuietStatus;
  mediaBeforeQuietStatus = null;
  if (!before || (!before.mic && !before.camera)) return;
  const result = await liveKitService.setLocalMedia(before);
  if (gen !== statusMediaGeneration) return;
  store().setLocalMuted(!result.mic);
  store().setLocalCameraOff(!result.camera);
}

/** Bouton micro ou caméra pressé hors Disponible : on explique au lieu d'allumer. */
function blockMediaForStatus(device: 'micro' | 'caméra'): boolean {
  const status = useGameStore.getState().localStatus;
  if (status === 'available') return false;
  const label = STATUS_NAMES[status];
  EventBus.emit('ui:toast', {
    message: `Tu es en ${label} : repasse en Disponible pour activer ${device === 'micro' ? 'ton micro' : 'ta caméra'}.`,
    tone: 'info',
    bypassFocus: true,
  });
  return true;
}

const STATUS_NAMES: Record<UserStatus, string> = {
  available: 'Disponible',
  away: 'Absent',
  busy: 'Occupé',
  focus: 'Focus',
};

/**
 * Connexion LiveKit de la zone — fetch token puis connectToZone().
 * room.connect() attend RoomEvent.Connected : pas de setTimeout hardcodé.
 */
async function connectZoneMedia(tenantId: string, slug: string, jwt: string, applyLobbyChoices: boolean): Promise<void> {
  const store = useGameStore.getState;
  try {
    const res = await fetch(
      `${API_BASE}/media/livekit-token?tenantId=${encodeURIComponent(tenantId)}&zoneSlug=${encodeURIComponent(slug)}`,
      { headers: { Authorization: `Bearer ${jwt}`, 'X-Tenant-ID': tenantId } },
    );
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const { token, url } = await res.json() as { token: string; url: string };
    await liveKitService.connectToZone(token, url);
    store().setLiveKitConnected(true);

    // Reconnexion : micro et caméra restent coupés (jamais réactivés sans
    // action de la personne).
    if (!applyLobbyChoices) {
      store().setLocalMuted(true);
      store().setLocalCameraOff(true);
      return;
    }

    // Appliquer les choix faits dans LobbyPage — connectToZone() laisse tout
    // coupé par défaut (micro : warm-up puis mute ; caméra : jamais activée).
    // Échec individuel (permission retirée entre le lobby et l'entrée en
    // zone, etc.) → repli sur coupé/off plutôt que de faire échouer toute
    // la connexion LiveKit (déjà réussie à ce stade).
    const { audioEnabled, videoEnabled } = store().settings;
    // Absent / Occupé / Focus : rien n'est allumé, les choix du lobby
    // s'appliqueront au retour en Disponible.
    if (store().localStatus !== 'available') {
      mediaBeforeQuietStatus = { mic: audioEnabled, camera: videoEnabled };
      store().setLocalMuted(true);
      store().setLocalCameraOff(true);
      return;
    }
    let micEnabled = false;
    if (audioEnabled) {
      try {
        await liveKitService.unmute();
        micEnabled = true;
      } catch (err) {
        console.warn('[GameBridge] activation micro (préférence lobby) échouée:', err);
      }
    }
    store().setLocalMuted(!micEnabled);

    let camEnabled = false;
    if (videoEnabled) {
      try {
        await liveKitService.setCameraEnabled(true);
        camEnabled = true;
      } catch (err) {
        console.warn('[GameBridge] activation caméra (préférence lobby) échouée:', err);
      }
    }
    store().setLocalCameraOff(!camEnabled);
  } catch (err) {
    console.warn('[GameBridge] LiveKit zone connection failed:', err);
  }
}

/**
 * Déconnexion (menu de l'avatar, écran « Campus ouvert dans une autre
 * fenêtre »). Fonction et pas seulement un événement : les écouteurs de
 * GameBridge n'existent que tant que le jeu est affiché (destroyGameBridge
 * au démontage), or SessionReplacedPage s'affiche jeu démonté.
 */
export async function logout(): Promise<void> {
  const store = useGameStore.getState;
  if (store().authState === 'unauthenticated') return;
  const jwt = store().jwt;
  try {
    // Révoque la session (jti) et efface le cookie HttpOnly. Un échec réseau
    // ne doit pas bloquer la déconnexion locale : le JWT est de toute façon
    // oublié côté client juste après.
    await fetch(`${API_BASE}/auth/logout`, {
      method: 'POST',
      credentials: 'include',
      headers: { Authorization: `Bearer ${jwt}`, 'X-Tenant-ID': getTenantSlug() },
    });
  } catch (err) {
    console.warn('[GameBridge:auth] logout API error:', err);
  }
  store().setAuthState('unauthenticated');
  colyseusClient.disconnect();
  void liveKitService.disconnectFromZone();
  store().setJwt(null as unknown as string);
  store().clearCurrentZone();
}

export function destroyGameBridge(): void {
  stopReconnecting();
  EventBus.removeAllListeners();
  void colyseusClient.disconnect();
  liveKitService.disconnectFromZone();
  liveKitService.disconnectFromBroadcast();
  mediaBeforeQuietStatus = null;
  statusMediaGeneration++;
  initialized = false;
}
